import { useState } from 'react';
import { BuildingIcon } from '@kilat/ui';

import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { useI18n } from '../lib/use-i18n.js';

/** Decorative disclosure caret — not copy, so not translated. */
const CHEVRON_DOWN_GLYPH = '▾';

/**
 * Top-bar outlet switcher.
 *
 * Single-outlet operators see a plain non-interactive label (no menu chrome,
 * keeps the UI clean). Multi-outlet operators get a small dropdown.
 *
 * Premium logical: when there's only one option there's no menu to open —
 * showing a dropdown for one choice is sub-premium UI noise.
 */
export function OutletSwitcher() {
  const { outlets, outletId, setOutletId, loading } = useActiveOutlet();
  const t = useI18n();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <span style={{ color: 'var(--color-on-surface-muted)', fontSize: 'var(--text-meta)' }}>
        …
      </span>
    );
  }
  if (outlets.length === 0) {
    return (
      <span style={{ color: 'var(--color-on-surface-muted)', fontSize: 'var(--text-meta)' }}>
        {t.t('dashboard.outletSwitcher.none')}
      </span>
    );
  }

  const current = outlets.find((o) => o.id === outletId) ?? outlets[0];
  if (!current) return null;

  if (outlets.length === 1) {
    // A single outlet has nothing to switch to, but the operator still needs to
    // know WHICH shop this dashboard is showing — especially a manager covering
    // two sites on one login. It used to render as a bare grey span floating
    // beside the nav, reading as stray text rather than as "you are here".
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.45em',
          fontSize: 'var(--text-meta)',
          fontWeight: 600,
          color: 'var(--color-on-surface)',
          background: 'var(--color-surface-sunken, rgba(11,41,31,0.05))',
          border: '1px solid var(--color-border, rgba(11,41,31,0.08))',
          borderRadius: 'var(--radius-pill)',
          padding: '0.3em 0.85em',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, opacity: 0.6 }}>
          <BuildingIcon size={13} />
        </span>
        {/* The name truncates, not the chip: a flex container's bare text can't
            take an ellipsis, so on a phone the header clipped the chip itself
            mid-word ("Demo ("). */}
        <span style={outletNameStyle}>{current.name}</span>
      </span>
    );
  }

  return (
    <div style={{ position: 'relative', minWidth: 0, maxWidth: '100%', display: 'flex' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
          fontSize: 'var(--text-meta)',
          fontWeight: 500,
          cursor: 'pointer',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <span style={outletNameStyle}>{current.name}</span>
        <span aria-hidden style={{ fontSize: 10, opacity: 0.6 }}>
          {CHEVRON_DOWN_GLYPH}
        </span>
      </button>
      {open ? (
        <>
          {/* Click-off catcher */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
            aria-hidden
          />
          <ul
            role="listbox"
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              zIndex: 11,
              listStyle: 'none',
              margin: 0,
              padding: 'var(--space-1)',
              minWidth: 220,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-3)',
              display: 'grid',
              gap: 2,
            }}
          >
            {outlets.map((o) => {
              const selected = o.id === outletId;
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setOutletId(o.id);
                      setOpen(false);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      background: selected ? 'rgba(13,122,79,0.06)' : 'transparent',
                      color: 'var(--color-on-surface)',
                      fontSize: 'var(--text-meta)',
                      fontWeight: selected ? 500 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {o.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}

const outletNameStyle: React.CSSProperties = {
  minWidth: 0,
  maxWidth: '22ch',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
